<script setup lang="ts">
import {cn} from "@/shared/lib/utils.ts";
import {provide, reactive, ref, watch} from "vue";
import {GridProps, gridVariants} from "@/shared/components/ui/helper";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import VStack from "@/shared/components/ui/helper/VStack.vue";
import {faSpinnerThird} from "@fortawesome/pro-solid-svg-icons";
import {ObjectSchema, ValidationError} from "yup";
import {FormContext, FormContextSymbol, FormInstance, FormValidationError, FormValidationSource} from './index.ts'
import {isEqual} from 'lodash'
import {useI18n} from "vue-i18n"; // or use your preferred deep comparison utility

interface FormProps extends GridProps {
  debug?: boolean
  loading?: boolean
  validation?: ObjectSchema<any>
  errors?: Record<string, FormValidationError>
}

const {t} = useI18n()
const props = withDefaults(defineProps<FormProps>(), {
  debug: false,
});
const emits = defineEmits<{
  (e: 'submit', values: object): void
}>()
const modelValue = defineModel<Object>()
const internalErrors = ref<Record<string, FormValidationError>>(props.errors || {})

//Expose the schema, the v-model and the errors to the v-validation directive
provide(FormContextSymbol, {
  validation: props.validation,
  value: modelValue,
  errors: internalErrors
} as FormContext)

const validate = async (): Promise<boolean> => {
  try {
    if (!props.validation) return true
    await props.validation.validate(modelValue.value, {abortEarly: false})
    internalErrors.value = {} //Clear all error, server errors are removed
    return true
  } catch (err) {
    if (err instanceof ValidationError) {
      const formErrors: Record<string, FormValidationError> = {}
      err.inner.forEach((error) => {
        if (error.path) {
          formErrors[error.path] = {
            label: error.message,
            source: FormValidationSource.CLIENT
          }
        }
      })
      // Merge validation errors with existing server errors
      internalErrors.value = {...internalErrors.value, ...formErrors}
    }
    return false
  }
}

const submit = async (): Promise<void> => {
  const isValid = await validate()
  if (isValid) {
    emits('submit', modelValue.value)
  } else {
    console.warn("Form is not valid")
  }
}

const handleSubmit = async (e: Event) => {
  e.preventDefault()
  await submit()
}

// Helper function to find changed paths
function findChangedPaths(newObj: any, oldObj: any, path: string = ''): string[] {
  const paths: string[] = []

  // Ensure both arguments are objects, or handle edge cases
  if (typeof newObj !== 'object' || newObj === null || typeof oldObj !== 'object' || oldObj === null) {
    if (!isEqual(newObj, oldObj)) {
      paths.push(path)
    }
    return paths
  }

  // Safe handling for null or undefined
  const allKeys = new Set([
    ...Object.keys(newObj || {}),
    ...Object.keys(oldObj || {}),
  ])

  for (const key of allKeys) {
    const newValue = newObj?.[key]
    const oldValue = oldObj?.[key]
    const currentPath = path ? `${path}.${key}` : key

    if (!isEqual(newValue, oldValue)) {
      if (typeof newValue !== 'object' || newValue === null || typeof oldValue !== 'object' || oldValue === null) {
        paths.push(currentPath)
      } else {
        paths.push(...findChangedPaths(newValue, oldValue, currentPath))
      }
    }
  }

  return paths
}

//If you get a ref for the form you can cast to FormInstance and call these method from your script
defineExpose({
  validate,
  submit
} as FormInstance)

const safeClone = (obj: any) => {
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (e) {
    return ""
  }
}

const previousValue = ref<any>(safeClone(modelValue.value))
watch(() => modelValue.value, () => {
  const changedPaths = findChangedPaths(modelValue.value, previousValue.value)

  // Clear errors only for changed paths
  changedPaths.forEach(path => {
    if (internalErrors.value[path]) {
      delete internalErrors.value[path]
    }
  })
  previousValue.value = JSON.parse(JSON.stringify(modelValue.value))
}, {deep: true})

watch(() => props.errors, (newErrors) => {
  if (newErrors && Object.keys(newErrors).length > 0) {
    const clientErrors = Object.entries(props.errors || {}).reduce((acc, [key, error]) => {
      if (error.source === FormValidationSource.CLIENT) {
        acc[key] = error
      }
      return acc
    }, {} as Record<string, FormValidationError>)
    internalErrors.value = {...clientErrors, ...newErrors}
  }
}, {immediate: true})
</script>

<template>
  <VStack v-if="loading" class="h-96 text-slate-500 col-span-full" justify="center" align="center">
    <FontAwesomeIcon :icon="faSpinnerThird" spin size="2xl" class="text-slate-500"/>
    {{ t('shared.common.loading') }}
  </VStack>
  <form v-else @submit="handleSubmit" :class="cn(gridVariants({ columns }), 'form-input', props.class)" data-validation-root>
    <slot/>
    <template v-if="debug">
      <br>
      <pre>Form Values: {{ modelValue }}</pre>
      <pre v-if="Object.keys(internalErrors).length">Errors: {{ internalErrors }}</pre>
    </template>
  </form>
</template>